/* eslint-disable no-bitwise */
import type { BitReader } from './bitReader';
import { ESC_HCB } from './data';
import {
  decodeHuffman, get_scalefactor_huffman_tree, get_spectrum_huffman_tree_1,
  get_spectrum_huffman_tree_2, get_spectrum_huffman_tree_3, get_spectrum_huffman_tree_4,
  get_spectrum_huffman_tree_5, get_spectrum_huffman_tree_6, get_spectrum_huffman_tree_7,
  get_spectrum_huffman_tree_8, get_spectrum_huffman_tree_9, get_spectrum_huffman_tree_10,
  get_spectrum_huffman_tree_11, type HuffmanTree,
} from './huffman';


export function decodeScaleFactor(bitReader: BitReader): number {
  return decodeHuffman(bitReader, get_scalefactor_huffman_tree()) - 60;
}


export function getHuffmanTree(codebookIndex: number): HuffmanTree {
  switch(codebookIndex) {
    case 1: return get_spectrum_huffman_tree_1();
    case 2: return get_spectrum_huffman_tree_2();
    case 3: return get_spectrum_huffman_tree_3();
    case 4: return get_spectrum_huffman_tree_4();
    case 5: return get_spectrum_huffman_tree_5();
    case 6: return get_spectrum_huffman_tree_6();
    case 7: return get_spectrum_huffman_tree_7();
    case 8: return get_spectrum_huffman_tree_8();
    case 9: return get_spectrum_huffman_tree_9();
    case 10: return get_spectrum_huffman_tree_10();
    case 11: return get_spectrum_huffman_tree_11();
    default: throw new Error(`Invalid codebookIndex: ${codebookIndex}`);
  }
}


function isUnsignedCodebook(codebookIndex: number): boolean {
  switch(codebookIndex) {
    case 1: return false;
    case 2: return false;
    case 3: return true;
    case 4: return true;
    case 5: return false;
    case 6: return false;
    case 7: return true;
    case 8: return true;
    case 9: return true;
    case 10: return true;
    case 11: return true;
    default: throw new Error(`Invalid codebookIndex: ${codebookIndex}`);
  }
}


function getCodebookDimension(codebookIndex: number): 2 | 4 {
  switch(codebookIndex) {
    case 1: return 4;
    case 2: return 4;
    case 3: return 4;
    case 4: return 4;
    case 5: return 2;
    case 6: return 2;
    case 7: return 2;
    case 8: return 2;
    case 9: return 2;
    case 10: return 2;
    case 11: return 2;
    default: throw new Error(`Invalid codebookIndex: ${codebookIndex}`);
  }
}

function getLargestAbsoluteValue(codebookIndex: number): number {
  switch(codebookIndex) {
    case 1: return 1;
    case 2: return 1;
    case 3: return 2;
    case 4: return 2;
    case 5: return 4;
    case 6: return 4;
    case 7: return 7;
    case 8: return 7;
    case 9: return 12;
    case 10: return 12;
    case 11: return 16;
    default: throw new Error(`Invalid codebookIndex: ${codebookIndex}`);
  }
}


export function getSpectralDataValues(idx: number, codebookIndex: number): number[] {
  const isUnsigned = isUnsignedCodebook(codebookIndex);
  const dimension = getCodebookDimension(codebookIndex);
  const lav = getLargestAbsoluteValue(codebookIndex);

  let mod: number;
  let off: number;

  if (isUnsigned) {
    mod = lav + 1;
    off = 0;
  } else {
    mod = 2 * lav + 1;
    off = lav;
  }

  if (dimension === 4) {
    const w = Math.floor(idx / (mod * mod * mod)) - off;
    idx -= (w + off) * (mod * mod * mod);
    const x = Math.floor(idx / (mod * mod)) - off;
    idx -= (x + off) * (mod * mod);
    const y = Math.floor(idx / mod) - off;
    idx -= (y + off) * mod;
    const z = idx - off;
    return [w, x, y, z];
  } else {
    const y = Math.floor(idx / mod) - off;
    idx -= (y + off) * mod;
    const z = idx - off;
    return [y, z];
  }
}


export function readEscapeSequence(bitReader: BitReader): number {
  let nBits = 4;
  while (bitReader.readBits(1) === 1) {
    nBits++;
  }

  return (1 << nBits) + bitReader.readBits(nBits);
}


export function decodeSpectralData(bitReader: BitReader, codebookIndex: number): number[] {
  const idx = decodeHuffman(bitReader, getHuffmanTree(codebookIndex));
  const values = getSpectralDataValues(idx, codebookIndex);

  if (isUnsignedCodebook(codebookIndex)) {
    // If the codebook is unsigned, there is one bit for each non-zero value to read.
    // If the bit it 1, then the corresponding value should be negative.
    // For example, if we have the values [2, 0, 0, 3], we need to read 2 bits XY.
    // If X is 1, 2 should become -2. If Y is 1, 3 should become -3
    for (let i = 0; i < values.length; i++) {
      if (values[i] !== 0) {
        if (bitReader.readBits(1) === 1) {
          values[i] = -values[i]!;
        }
      }
    }
  }

  if (codebookIndex === ESC_HCB) {
    // The codebook 11 has a special meaning for the value 16 that is an escape
    // code used to encode values larger than 15. When the 16 value is found, we
    // need to read N 1 bits followed by a 0 bit. Then, we read a value X of N+4
    // bits and we obtain the escaped value 2^(N+4) + X
    // If the value is -16, we adjust the sign of the escaped value accordingly
    for (let i = 0; i < values.length; i++) {
      if (values[i] === -16 || values[i] === 16) {
        const escapedValue = readEscapeSequence(bitReader);
        if (values[i]! < 0) {
          values[i] = -escapedValue;
        } else {
          values[i] = escapedValue;
        }
      }
    }
  }

  return values;
}
